import { useState } from "react";
import { Loader2, KeyRound, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/forms/fields";
import { apiClient, formatApiErrorDetail } from "@/services/api";
import { PASSWORD_RULES } from "@/lib/passwordPolicy";

const RULES = PASSWORD_RULES;

const EMPTY = { current: "", next: "", confirm: "" };

export function ChangePasswordDrawer({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const strongEnough = RULES.every((r) => r.test(form.next));
  const confirmError = form.confirm && form.confirm !== form.next ? "Passwords do not match" : "";
  const canSave = form.current && strongEnough && form.next === form.confirm && !saving;

  const close = () => {
    setForm(EMPTY);
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password changed successfully");
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(v) => (v ? onOpenChange(true) : close())}
      icon={KeyRound}
      title="Change Password"
      description="Update the password for your account."
      footer={
        <>
          <Button variant="outline" onClick={close} data-testid="change-password-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="change-password-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change password"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field label="Current Password" required>
          <Input
            type="password"
            autoComplete="current-password"
            value={form.current}
            onChange={set("current")}
            data-testid="change-password-current"
          />
        </Field>
        <Field label="New Password" required>
          <Input
            type="password"
            autoComplete="new-password"
            value={form.next}
            onChange={set("next")}
            data-testid="change-password-new"
          />
        </Field>
        <ul className="space-y-1.5">
          {RULES.map((r) => {
            const ok = r.test(form.next);
            return (
              <li key={r.key} className="flex items-center gap-2 text-xs" data-testid={`pw-rule-${r.key}`}>
                {ok ? (
                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                ) : (
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                <span className={ok ? "text-emerald-600" : "text-muted-foreground"}>{r.label}</span>
              </li>
            );
          })}
        </ul>
        <Field label="Confirm New Password" required error={confirmError}>
          <Input
            type="password"
            autoComplete="new-password"
            value={form.confirm}
            onChange={set("confirm")}
            data-testid="change-password-confirm"
          />
        </Field>
      </div>
    </Drawer>
  );
}
